import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  setupAdmin: (data) => api.post("/auth/setup-admin", data),
  checkSetup: () => api.get("/auth/check-setup"),
  me: () => api.get("/auth/me"),
  register: (data) => api.post("/auth/register", data),
};

// Users
export const usersAPI = {
  getAll: () => api.get("/users"),
  getMechanics: () => api.get("/users/mechanics"),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  getRolePermissions: () => api.get("/role-permissions"),
};

// Customers
export const customersAPI = {
  getAll: () => api.get("/customers"),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post("/customers", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Excavators
export const excavatorsAPI = {
  getAll: () => api.get("/excavators"),
  getOne: (id) => api.get(`/excavators/${id}`),
  create: (data) => api.post("/excavators", data),
  update: (id, data) => api.put(`/excavators/${id}`, data),
  delete: (id) => api.delete(`/excavators/${id}`),
};

// Parts
export const partsAPI = {
  getAll: () => api.get("/parts"),
  getOne: (id) => api.get(`/parts/${id}`),
  create: (data) => api.post("/parts", data),
  update: (id, data) => api.put(`/parts/${id}`, data),
  delete: (id) => api.delete(`/parts/${id}`),
};

// Jobs
export const jobsAPI = {
  getAll: (params = {}) => api.get("/jobs", { params }),
  getOne: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post("/jobs", data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  uploadPhoto: async (id, file) => {
    // Compress image before upload
    const { compressImage } = await import('./imageUtils');
    const compressed = await compressImage(file, 200);
    
    // Convert base64 to blob
    const response = await fetch(compressed);
    const blob = await response.blob();
    
    const formData = new FormData();
    formData.append("file", blob, file.name);
    return api.post(`/jobs/${id}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deletePhoto: (id, photoIndex) => api.delete(`/jobs/${id}/photos/${photoIndex}`),
  addSignature: (id, signature) => api.post(`/jobs/${id}/signature`, { signature }),
  checkIn: (id, data) => api.post(`/jobs/${id}/check-in`, data),
  checkOut: (id, data) => api.post(`/jobs/${id}/check-out`, data),
};

// Deliveries
export const deliveriesAPI = {
  getAll: (params = {}) => api.get("/deliveries", { params }),
  getOne: (id) => api.get(`/deliveries/${id}`),
  create: (data) => api.post("/deliveries", data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  delete: (id) => api.delete(`/deliveries/${id}`),
  checkIn: (id, data) => api.post(`/deliveries/${id}/check-in`, data),
  checkOut: (id, data) => api.post(`/deliveries/${id}/check-out`, data),
  getSuggestions: () => api.get("/deliveries/suggestions/fields"),
  recalculateDistances: () => api.post("/deliveries/recalculate-distances"),
};

// Drivers
export const driversAPI = {
  getAll: () => api.get("/drivers"),
};

// Job Types
export const jobTypesAPI = {
  getAll: () => api.get("/job-types"),
  add: (name) => api.post(`/job-types?name=${encodeURIComponent(name)}`),
  update: (oldName, newName) => api.put(`/job-types/${encodeURIComponent(oldName)}?new_name=${encodeURIComponent(newName)}`),
  delete: (name) => api.delete(`/job-types/${encodeURIComponent(name)}`),
};

// Invoices
export const invoicesAPI = {
  getAll: (status) => api.get("/invoices", { params: { status } }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices", data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get("/stats/dashboard"),
};

// Letterheads
export const letterheadsAPI = {
  getAll: () => api.get("/settings/letterheads"),
  getOne: (id) => api.get(`/settings/letterheads/${id}`),
  upload: async (name, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/settings/letterheads?name=${encodeURIComponent(name)}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  delete: (id) => api.delete(`/settings/letterheads/${id}`),
};

// Export
export const exportAPI = {
  download: async (entity) => {
    const response = await api.get(`/export/${entity}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = response.headers['content-disposition']?.split('filename=')[1] || `${entity}.xlsx`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Import
export const importAPI = {
  downloadTemplate: async (entity) => {
    const response = await api.get(`/import/template/${entity}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = response.headers['content-disposition']?.split('filename=')[1] || `${entity}_template.xlsx`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  upload: async (entity, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/${entity}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// PDRs (Pre-Delivery Requests)
export const pdrsAPI = {
  getAll: (params = {}) => api.get("/pdrs", { params }),
  getOne: (id) => api.get(`/pdrs/${id}`),
  create: (data) => api.post("/pdrs", data),
  update: (id, data) => api.put(`/pdrs/${id}`, data),
  delete: (id) => api.delete(`/pdrs/${id}`),
  uploadAttachments: (id, files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return api.post(`/pdrs/${id}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getAttachment: (id, attId) =>
    api.get(`/pdrs/${id}/attachments/${attId}`, { responseType: "blob" }),
  deleteAttachment: (id, attId) => api.delete(`/pdrs/${id}/attachments/${attId}`),
  exportXlsx: async (params = {}) => {
    const response = await api.get("/pdrs/export/xlsx", { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = response.headers['content-disposition']?.split('filename=')[1] || 'PDR_Export.xlsx';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadTemplate: async () => {
    const response = await api.get("/pdrs/template/xlsx", { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'PDR_Import_Template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  import: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post("/pdrs/import", formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadPdf: async (id, formNo) => {
    const response = await api.get(`/pdrs/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${formNo || 'PDR'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Job Orders
export const jobOrdersAPI = {
  getAll: (params = {}) => api.get("/job-orders", { params }),
  getOne: (id) => api.get(`/job-orders/${id}`),
  create: (data) => api.post("/job-orders", data),
  update: (id, data) => api.put(`/job-orders/${id}`, data),
  delete: (id) => api.delete(`/job-orders/${id}`),
  exportXlsx: async (params = {}) => {
    const response = await api.get("/job-orders/export/xlsx", { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = response.headers['content-disposition']?.split('filename=')[1] || 'JobOrders_Export.xlsx';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadPdf: async (id, orderNo) => {
    const response = await api.get(`/job-orders/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${orderNo || 'JO'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default api;
